'use client';
import {useRef} from 'react';
import {ChevronDown, Globe2, Star, Users2} from 'lucide-react';
import CosmicArtifact from '@/components/common/ui/decorations/CosmicArtifact';
import Tag from '@/components/common/ui/display/Tag';
import IconBox from '@/components/common/ui/display/IconBox';
import Galaxy from '@/components/common/ui/decorations/Galaxy';
import {useTranslations} from 'next-intl';
import Button from '@/components/common/ui/buttons/Button';

export default function Hero() {
  const buttonRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('pages.home.hero');

  const scrollToServices = () => {
    const sectionElement = document.getElementById('home-services');
    if (sectionElement) {
      const headerOffset = 80; // Adjust this value based on your header height
      const elementPosition = sectionElement.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="pt-20 pb-8">
      <div className="relative z-10 backdrop-blur-lg">
        {/* Cosmic artifacts background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute" style={{top: '10%', left: '5%'}}>
            <CosmicArtifact size="md" />
          </div>
          <div className="absolute" style={{top: '60%', right: '10%'}}>
            <CosmicArtifact size="lg" />
          </div>
          <div className="absolute" style={{bottom: '15%', left: '15%'}}>
            <CosmicArtifact size="xl" />
          </div>
          <div className="absolute" style={{top: '30%', right: '25%'}}>
            <CosmicArtifact size="md" />
          </div>
        </div>
        {/* Galaxy Animations */}
        <Galaxy
          size={400}
          color="rgba(147, 51, 234, 0.2)"
          animationDuration={28}
          style={{
            top: '50%',
            left: '5%',
            transform: 'translateY(-50%)'
          }}
        />
        <Galaxy
          size={200}
          color="rgba(139, 92, 246, 0.2)"
          animationDuration={22}
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        />
        <Galaxy
          size={300}
          color="rgba(124, 58, 237, 0.2)"
          animationDuration={25}
          style={{
            top: '0',
            right: '0'
          }}
        />

        <div className="mx-auto max-w-4xl text-center relative z-10">
          <Tag className="mb-4">{t('tag')}</Tag>
          <h1 className="mb-6 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-4xl font-bold text-transparent sm:text-5xl sm:leading-tight">
            {t('title')}
          </h1>
          <p className="text-xl text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto">
            {t('description')}
          </p>
          <div className="flex flex-col items-center">
            <div ref={buttonRef}>
              <Button
                className="mb-4 animate-pulse-slow"
                onClick={scrollToServices}
              >
                {t('ctaButton')}
              </Button>
            </div>

            <div className="relative h-12 w-6 float">
              <ChevronDown className="h-6 w-6 text-purple-600 dark:text-purple-400 absolute top-0 left-0 animate-bounce" />
              <ChevronDown className="h-6 w-6 text-purple-600 dark:text-purple-400 absolute top-3 left-0 animate-bounce delay-150" />
            </div>

            <div className="mt-8 w-full grid grid-cols-1 gap-8 sm:grid-cols-3">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <IconBox icon={Star} shape="rounded-full" size="md" />
                </div>
                <p className="font-semibold text-slate-900 dark:text-white">
                  {t('stat1')}
                </p>
              </div>
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <IconBox icon={Globe2} shape="rounded-full" size="md" />
                </div>
                <p className="font-semibold text-slate-900 dark:text-white">
                  {t('stat2')}
                </p>
              </div>
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <IconBox icon={Users2} shape="rounded-full" size="md" />
                </div>
                <p className="font-semibold text-slate-900 dark:text-white">
                  {t('stat3')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
